/**
 * A modal built on the native `<dialog>` element.
 *
 * Deliberately not a hand-rolled overlay: the browser gives focus trapping,
 * Escape, inert background content and top-layer stacking for free, and those
 * are exactly the parts a hand-rolled one gets wrong. WCAG 2.1 AA is a stated
 * requirement, so the free correct implementation wins.
 *
 * Shared rather than per-feature. Employees and Providers each had their own
 * copy and they had already drifted apart (one grew a description and a size,
 * the other did not); Imports would have made three. A dialog belongs to no
 * single screen, which is exactly why it belongs here.
 */

import { X } from "lucide-react"
import * as React from "react"

import { Button, SignageBand } from "@/components/ui"

export function Modal({
  title,
  description,
  onClose,
  children,
  footer,
  size = "lg",
}: {
  title: string
  description?: string
  onClose: () => void
  children?: React.ReactNode
  footer?: React.ReactNode
  size?: "sm" | "md" | "lg"
}) {
  const ref = React.useRef<HTMLDialogElement>(null)
  const titleId = React.useId()
  const descriptionId = React.useId()

  React.useEffect(() => {
    const dialog = ref.current
    if (!dialog?.showModal) return
    dialog.showModal()
    return () => dialog.close()
  }, [])

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onCancel={(event) => {
        // Escape: close through React so the parent's state stays the truth.
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose()
      }}
      // `m-auto` is load-bearing: the browser centres a modal dialog with its
      // own `margin: auto`, and Tailwind's preflight zeroes every element's
      // margin, which left dialogs pinned to the viewport's top-left corner.
      // `h-fit` keeps the auto margins splitting real space, so the card sits
      // centred and grows with its content up to the height cap below.
      // `overflow-hidden` is what makes the band's square top corners take
      // the card's radius; without it the red runs past the rounding.
      // `text-card-foreground` is load-bearing too: a `<dialog>` gets the UA's
      // `color: CanvasText`, which is black regardless of theme, so any text
      // inside that states no colour of its own renders 1.23:1 on the dark
      // card. The dialog must declare the theme's foreground itself.
      className={`m-auto h-fit w-[calc(100%-2rem)] overflow-hidden rounded-2xl border bg-card p-0 text-card-foreground shadow-elevated backdrop:bg-black/40 ${
        size === "sm" ? "max-w-md" : size === "md" ? "max-w-xl" : "max-w-3xl"
      }`}
    >
      {/* `--column-inset` is the dialog's own body padding, `px-5` below, and
          it is declared here so the band's title starts exactly where the
          text under it starts. The two must move together; that is the whole
          rule, and it is pinned in `ui.test.tsx` because a stylesheet cannot
          state a tie between two files. */}
      <div className="flex max-h-[85dvh] flex-col [--column-inset:1.25rem]">
        {/* The same rim-ruled band the pages wear, from the same component and
            at the dialog scale, so a dialog cannot drift away from a page
            header. The close button rides in the band's trailing slot, which
            sits clear of the red on the card's own surface: white on the CI
            red is only within AA as large text, and an icon is not large text.
            It fills the height under the rim rather than keeping its own 44px,
            which would overhang a 32px band; 44px wide holds the target. */}
        <SignageBand
          title={title}
          titleId={titleId}
          as="h2"
          size="dialog"
          trailing={
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label="Close"
              className="h-full w-11 rounded-md"
            >
              <X className="h-4 w-4" aria-hidden />
            </Button>
          }
        />
        {description && (
          <p
            id={descriptionId}
            // The divider under the description belongs to the body that
            // follows it; a description-only dialog draws none, or it would
            // stack against the footer's own top border as a double line.
            className={`shrink-0 px-5 pb-4 pt-3 text-sm text-muted-foreground ${
              children ? "border-b" : ""
            }`}
          >
            {description}
          </p>
        )}

        {/* A dialog with no body (a bare confirm: description and buttons)
            renders no body strip at all, rather than an empty padded band
            between the description and the footer. */}
        {children && (
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {children}
          </div>
        )}

        {footer && (
          <div className="flex flex-col-reverse gap-2 border-t px-5 py-4 sm:flex-row sm:justify-end">
            {footer}
          </div>
        )}
      </div>
    </dialog>
  )
}

export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  confirmVariant = "danger",
  onConfirm,
  onClose,
  pending,
  error,
  children,
  size = "sm",
}: {
  title: string
  description?: string
  confirmLabel: string
  /** Red by default. `primary` is for a consequential act that is not a loss. */
  confirmVariant?: "danger" | "primary"
  onConfirm: () => void
  onClose: () => void
  pending?: boolean
  error?: string | null
  children?: React.ReactNode
  size?: "sm" | "md" | "lg"
}) {
  return (
    <Modal
      title={title}
      {...(description ? { description } : {})}
      onClose={onClose}
      size={size}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant={confirmVariant} onClick={onConfirm} loading={pending}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {/* No body at all when there is nothing to say beyond the description:
          a fragment is truthy even when empty, and Modal keys its body strip
          off the children's presence. */}
      {children || error ? (
        <>
          {children}
          {error && (
            <p className="mt-3 text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </>
      ) : undefined}
    </Modal>
  )
}
